import { supabase } from '@/shared/lib/supabase';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * paymentArrangementService — payment arrangements agreed with a client.
 *
 * An arrangement holds the dunning ladder (grace / suspend / cancel and their
 * emails) for a stated period, records what the client promised, and breaks by
 * itself when a promised date passes unpaid. It is NOT the permanent
 * `customer_link.dunning_policy` exemption.
 *
 * Reads go through `v_admin_payment_arrangement` (owner-run, staff-scoped) so
 * every staff member can see an agreement; writes go through admin-only Edge
 * Functions. See migration 20260901133247.
 */

export type ArrangementStatus = 'active' | 'completed' | 'broken' | 'cancelled';
export type InstallmentStatus = 'pending' | 'met' | 'missed';

export interface ArrangementRow {
  id: string;
  number: string;
  customer_id: string;
  customer_link_id: string | null;
  status: ArrangementStatus;
  reason: string;
  holds_until: string;
  agreed_at: string;
  agreed_by_name: string | null;
  agreed_by_email: string | null;
  broken_at: string | null;
  broken_reason: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  cpanel_username: string | null;
  installments_total: number;
  installments_met: number;
  promised_total: number;
  next_due_date: string | null;
}

export interface InstallmentRow {
  id: string;
  arrangement_id: string;
  due_date: string;
  amount: number;
  status: InstallmentStatus;
  met_at: string | null;
}

export interface CreateArrangementInput {
  customer_id: string;
  customer_link_id?: string | null;
  reason: string;
  holds_until: string;
  installments: Array<{ due_date: string; amount: number }>;
  invoice_ids?: string[];
}

interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

async function invokeEf<TRes>(name: string, body: Record<string, unknown>): Promise<TRes> {
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  });

  if (error) {
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.clone === 'function') {
      try {
        const parsed = (await ctx.clone().json()) as ApiError;
        if (parsed.code) {
          throw new EdgeFunctionError(
            parsed.code,
            parsed.message || parsed.code,
            ctx.status,
            parsed.details as Record<string, unknown> | undefined,
          );
        }
      } catch (e) {
        if (e instanceof EdgeFunctionError) throw e;
      }
    }
    throw new Error(error.message);
  }
  return data as TRes;
}

export const paymentArrangementService = {
  /** Every arrangement for one customer, newest agreement first. */
  async listForCustomer(customerId: string): Promise<ArrangementRow[]> {
    const { data, error } = await supabase
      .from('v_admin_payment_arrangement')
      .select('*')
      .eq('customer_id', customerId)
      .order('agreed_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as ArrangementRow[];
  },

  /** The promised instalments behind one arrangement, in date order. */
  async listInstallments(arrangementId: string): Promise<InstallmentRow[]> {
    const { data, error } = await supabase
      .from('payment_arrangement_installment')
      .select('id, arrangement_id, due_date, amount, status, met_at')
      .eq('arrangement_id', arrangementId)
      .order('due_date', { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as InstallmentRow[];
  },

  async create(input: CreateArrangementInput) {
    return invokeEf<{
      ok: true;
      arrangement_id: string;
      number: string;
      holds_until: string;
      promised_total: number;
      installments: number;
      covers_invoices: number;
    }>('payment-arrangement-create', input as unknown as Record<string, unknown>);
  },

  async cancel(arrangementId: string, reason?: string) {
    return invokeEf<{ ok: true; number: string; previous_status: string }>(
      'payment-arrangement-cancel',
      { arrangement_id: arrangementId, reason },
    );
  },
};
